import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { AuthService } from 'app/core/services/auth.service';
import { NavigationItemInterface } from 'app/layout/components/navigation/models/navigation-item-interface';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TenantSelectorComponent } from './tenant-selector/tenant-selector.component';
import { NavGroupComponent } from './nav-group/nav-group.component';
import { NavCollapsableComponent } from './nav-collapsable/nav-collapsable.component';
import { NavItemComponent } from './nav-item/nav-item.component';

@Component({
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-navigation',
  styleUrls: ['./navigation.component.scss'],
  templateUrl: './navigation.component.html',
  imports: [TenantSelectorComponent, NavGroupComponent, NavCollapsableComponent, NavItemComponent],
})
export class NavigationComponent implements OnInit {
  @Input() public navigation: NavigationItemInterface[] | undefined;
  public allowedCustomerUuids: string[];
  public allowedLaboratoryUuids: string[];

  private _authService = inject(AuthService);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _fuseNavigationService = inject(FuseNavigationService);
  private _unsubscribeAll: Subject<void>;

  public constructor() {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  public ngOnInit(): void {
    // Load the navigation either from the input or from the service
    this.navigation = this.navigation || this._fuseNavigationService.getCurrentNavigation();

    this._authService.isDoneLoading$.pipe(takeUntil(this._unsubscribeAll)).subscribe((isDoneLoadingFlag) => {
      if (isDoneLoadingFlag) {
        // list of owned laboratories
        this.allowedCustomerUuids = this._authService.allowedCustomerUuids;
        this.allowedLaboratoryUuids = this._authService.allowedLaboratoryUuids;

        // Mark for check
        this._changeDetectorRef.markForCheck();
      }
    });

    // Subscribe to the current navigation changes
    this._fuseNavigationService.onNavigationChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
      // Load the navigation
      this.navigation = this._fuseNavigationService.getCurrentNavigation();

      // Mark for check
      this._changeDetectorRef.markForCheck();
    });

    // Subscribe to navigation item
    merge(
      this._fuseNavigationService.onNavigationItemAdded,
      this._fuseNavigationService.onNavigationItemUpdated,
      this._fuseNavigationService.onNavigationItemRemoved,
    )
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        // Mark for check
        this._changeDetectorRef.markForCheck();
      });

    this._fuseNavigationService.onNavigationRegistered.subscribe(() => {
      this.navigation = this._fuseNavigationService.getCurrentNavigation();

      // Mark for check
      this._changeDetectorRef.markForCheck();
    });
  }
}
